/**
 * 运行环境判断模块  utils/env.js
 *
 * 两层判断：
 *   第一层 编译期：小程序 / App，用 #ifdef 写死，不属于当前平台的代码自动剔除
 *   第二层 运行期：H5 包跑在哪个"容器"里，只能运行时判断
 *                  —— 优先用全局桥对象（最可靠），UA 作为兜底
 *
 * 用法：
 *   import { detectEnv, ENV, isInWechat } from '@/utils/env'
 *   const env = detectEnv()
 *   if (env === ENV.NATIVE_APP_WEBVIEW) { ... }
 *
 *   // 需要可靠判断"是否在微信小程序 web-view"时，用异步版：
 *   const env = await detectEnvAsync()
 */

/* ------------------------------------------------------------------ *
 * 环境枚举
 * ------------------------------------------------------------------ */
export const ENV = {
  MP_WEIXIN:              'mp-weixin',          // 微信小程序原生（编译期确定）
  UNIAPP_APP:             'uniapp-app',         // uniapp 编译的原生 App（编译期确定）
  WX_MINIPROGRAM_WEBVIEW: 'wx-mp-webview',      // H5 跑在微信小程序的 web-view 里
  WECHAT_BROWSER:         'wechat-browser',     // H5 跑在微信内置浏览器（公众号网页）
  NATIVE_APP_WEBVIEW:     'native-app-webview', // H5 跑在"智管家原生 App"的 webview 里
  BROWSER:                'browser',            // 普通浏览器
  UNKNOWN:                'unknown'             // 兜底
}

/* ------------------------------------------------------------------ *
 * H5 容器判断（只在 H5 端编译进来）
 * ------------------------------------------------------------------ */


/**
 * 智管家原生 App 的识别配置
 *   bridgeKey: App 往 window 上注入的桥对象名（service/jsbridge.js 中使用的 WebViewJavascriptBridge）
 *   uaFlag:    App 塞进 userAgent 的自定义标识（小写），需 App 团队确认后补充
 */
const NATIVE_APP = {
  bridgeKey: 'WebViewJavascriptBridge',
  uaFlag:    ''  // TODO: 与 App 团队确认后填入自定义 UA 标识（小写）
}

/**
 * 判断当前 H5 页面跑在哪个容器里（同步版）
 * 判断顺序：先判小程序 web-view，再判微信浏览器，最后判自家 App
 */
function detectH5Container() {
  const ua       = (navigator.userAgent || '').toLowerCase()
  const isWechat = /micromessenger/i.test(ua)
  const isMpUA   = /miniprogram/i.test(ua)
  const isMpFlag = window.__wxjs_environment === 'miniprogram'

  // 1. 微信小程序的 web-view —— 必须最先判（UA 同时含 micromessenger + miniprogram）
  if (isMpFlag || (isWechat && isMpUA)) {
    return ENV.WX_MINIPROGRAM_WEBVIEW
  }
  // 2. 微信内置浏览器（公众号网页，不是小程序）
  if (isWechat) {
    return ENV.WECHAT_BROWSER
  }
  // 3. 智管家原生 App 的 webview —— 优先桥对象，UA 兜底
  const hasUaFlag = NATIVE_APP.uaFlag && ua.indexOf(NATIVE_APP.uaFlag) > -1
  if (window[NATIVE_APP.bridgeKey] || hasUaFlag) {
    return ENV.NATIVE_APP_WEBVIEW
  }
  // 4. 普通浏览器
  return ENV.BROWSER
}


/* ------------------------------------------------------------------ *
 * 对外：同步判断
 * ------------------------------------------------------------------ */
/**
 * 同步获取当前环境。
 * H5 端判断"小程序 web-view"依赖 window.__wxjs_environment，
 * 该值在 WeixinJSBridgeReady 之后才一定有值，页面刚加载时可能漏判，
 * 需要可靠结果时请用 detectEnvAsync()。
 * @returns {string} ENV 中的枚举值
 */
export function detectEnv() {









  return detectH5Container()


  // eslint-disable-next-line no-unreachable
  return ENV.UNKNOWN
}

/* ------------------------------------------------------------------ *
 * 对外：异步判断（可靠判断微信小程序 web-view 时用）
 * ------------------------------------------------------------------ */
/**
 * 异步获取当前环境。
 * H5 端会等待 WeixinJSBridgeReady，确保 __wxjs_environment 已就绪；
 * 若不在微信里（不会触发 ready），300ms 超时后按"非小程序"判定。
 * @returns {Promise<string>}
 */
export function detectEnvAsync() {
  return new Promise((resolve) => {









    const finish = () => resolve(detectH5Container())

    if (window.__wxjs_environment === 'miniprogram') {
      finish(); return
    }
    if (!window.WeixinJSBridge || !window.WeixinJSBridge.invoke) {
      let done = false
      const handler = () => { if (!done) { done = true; finish() } }
      document.addEventListener('WeixinJSBridgeReady', handler, false)
      setTimeout(() => { if (!done) { done = true; finish() } }, 300)
    } else {
      finish()
    }
    return


    // eslint-disable-next-line no-unreachable
    resolve(ENV.UNKNOWN)
  })
}

/* ------------------------------------------------------------------ *
 * 对外：便捷布尔判断
 * ------------------------------------------------------------------ */
export const isMpWeixin         = () => detectEnv() === ENV.MP_WEIXIN
export const isUniappApp        = () => detectEnv() === ENV.UNIAPP_APP
export const isWxMpWebview      = () => detectEnv() === ENV.WX_MINIPROGRAM_WEBVIEW
export const isWechatBrowser    = () => detectEnv() === ENV.WECHAT_BROWSER
export const isNativeAppWebview = () => detectEnv() === ENV.NATIVE_APP_WEBVIEW
export const isBrowser          = () => detectEnv() === ENV.BROWSER

/**
 * 是否在"微信生态"内（小程序原生 / 小程序 web-view / 微信浏览器）
 * 可替代 common.ts 中的 isInWeChat()，判断更精确。
 */
export function isInWechat() {
  const e = detectEnv()
  return e === ENV.MP_WEIXIN ||
         e === ENV.WX_MINIPROGRAM_WEBVIEW ||
         e === ENV.WECHAT_BROWSER
}
